import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePageTitle, useHeaderActionsTarget } from '@/layouts/usePageTitle';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { Tag, BarChart3, Settings, PhoneCall, ClipboardPaste, Plus, Trash2, Save, Activity, Sun, PanelsTopLeft, Server, Gauge, ThermometerSun } from 'lucide-react';
import PlantSensorsPanel from '@/components/powerplants/detail/PlantSensorsPanel';
import InverterProductionCards from '@/components/powerplants/detail/InverterProductionCards';
import type { ProductionPeriod } from '@/api/inverterProduction';
import { fetchDetailCards, fetchDetailCharts, fetchPlantPowerDrillChart, fetchOpenMeteoExpectedHourly, fetchPvlibExpectedHourly } from '@/api/powerplantDetail';
import type { DetailCards, DetailCharts } from '@/api/powerplantDetail';
import {
  fetchPowerPlantDetailSolarPanelSummary,
  fetchPowerPlantManualForecast,
  savePowerPlantManualForecast,
  type ManualForecastRow,
  type SolarPanelSummary,
} from '@/api/powerplants';
import { fetchWeather } from '@/api/weather';
import type { WeatherData } from '@/api/weather';
import DetailStatCards from '@/components/powerplants/detail/DetailStatCards';
import InverterLineChart from '@/components/powerplants/detail/InverterLineChart';
import SmartloggerPanel from '@/components/powerplants/detail/SmartloggerPanel';
import PowerMeterPanel from '@/components/meters/PowerMeterPanel';
import EnergyFlowPage from '@/pages/monitoring/EnergyFlowPage';
import PerformanceChart from '@/components/powerplants/detail/PerformanceChart';
import ObjectTagsPanel from '@/components/tags/ObjectTagsPanel';
import PowerBarChart from '@/components/dashboard/PowerBarChart';
import { useI18n } from '@/i18n';
import SolarPanelInfoCard from '@/components/solar-panels/SolarPanelInfoCard';
import PlantSettingsModal from '@/components/powerplants/detail/PlantSettingsModal';
import PlantModbusLiveModal from '@/components/powerplants/detail/PlantModbusLiveModal';
import PlantContactCardsModal from '@/components/powerplants/detail/PlantContactCardsModal';
import DropdownMenu from '@/ui/common/DropdownMenu';
import { fetchPVGISEstimate } from '@/api/pvgis';
import toast from 'react-hot-toast';
import { useErrorToast, LoadRetry } from '@/lib/notify';

const MONTH_LABELS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'] as const;

type ForecastEditRow = {
  roofOrientation: string;
  months: string[];
};

function parseLocaleNumber(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed || trimmed === '-') return null;
  const normalized = trimmed.replace(/\s+/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatMonthValue(value: number | null): string {
  if (value === null || value === undefined) return '';
  return new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(value);
}

function parsePastedForecast(text: string): Array<{ roofOrientation: string; months: Array<number | null> }> {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  const parsedRows: Array<{ roofOrientation: string; months: Array<number | null> }> = [];

  for (const line of lines) {
    const cols = line.split('\t').map((col) => col.trim());
    const firstNumericIndex = cols.findIndex((col) => parseLocaleNumber(col) !== null);
    if (firstNumericIndex < 0) continue;

    const orientationCandidate = cols
      .slice(0, firstNumericIndex)
      .filter((col) => col.length > 0)
      .pop();

    if (!orientationCandidate) continue;
    if (orientationCandidate.toLowerCase() === 'toplam') continue;

    const months = Array.from({ length: 12 }, (_, index) => parseLocaleNumber(cols[firstNumericIndex + index] ?? ''));
    parsedRows.push({
      roofOrientation: orientationCandidate,
      months,
    });
  }

  return parsedRows;
}

function ManualForecastModal({
  open,
  onClose,
  plantUuid,
}: {
  open: boolean;
  onClose: () => void;
  plantUuid?: string;
}) {
  const currentYear = new Date().getFullYear();
  const [pasteText, setPasteText] = useState('');
  const [rows, setRows] = useState<ForecastEditRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pvgisMonths, setPvgisMonths] = useState<number[]>(Array.from({ length: 12 }, () => 0));
  const numberFormatter = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 });

  useEffect(() => {
    if (!open || !plantUuid) return;
    setLoading(true);
    Promise.all([
      fetchPowerPlantManualForecast(plantUuid, currentYear),
      fetchPVGISEstimate(plantUuid),
    ])
      .then(([manualForecast, pvgisEstimate]) => {
        setRows(
          manualForecast.rows.length
            ? manualForecast.rows.map((row: ManualForecastRow) => ({
                roofOrientation: row.roofOrientation,
                months: row.months.map((month) => formatMonthValue(month)),
              }))
            : [{ roofOrientation: 'Güney Doğu', months: Array.from({ length: 12 }, () => '') }],
        );

        const rawMonthly = (pvgisEstimate.estimate?.outputs?.monthly as any)?.fixed || pvgisEstimate.estimate?.outputs?.monthly || [];
        const normalizedMonths = Array.from({ length: 12 }, (_, index) => {
          const item = Array.isArray(rawMonthly)
            ? rawMonthly.find((entry: any) => Number(entry.month || 0) === index + 1) ?? rawMonthly[index]
            : null;
          return Number(Number(item?.E_m || 0).toFixed(2));
        });
        setPvgisMonths(normalizedMonths);
      })
      .catch(() => {
        toast.error('Manuel tahmin verisi alınamadı.');
        setRows([{ roofOrientation: 'Güney Doğu', months: Array.from({ length: 12 }, () => '') }]);
        setPvgisMonths(Array.from({ length: 12 }, () => 0));
      })
      .finally(() => setLoading(false));
  }, [open, plantUuid, currentYear]);

  const applyPaste = () => {
    const parsed = parsePastedForecast(pasteText);
    if (!parsed.length) {
      toast.error('Yapıştırılan veriden satır çıkarılamadı.');
      return;
    }
    setRows(
      parsed.map((row) => ({
        roofOrientation: row.roofOrientation,
        months: row.months.map((item) => formatMonthValue(item)),
      })),
    );
    toast.success(`${parsed.length} satır alındı.`);
  };

  const updateCell = (rowIndex: number, monthIndex: number, value: string) => {
    setRows((prev) => prev.map((row, index) => {
      if (index !== rowIndex) return row;
      const nextMonths = [...row.months];
      nextMonths[monthIndex] = value;
      return { ...row, months: nextMonths };
    }));
  };

  const updateOrientation = (rowIndex: number, value: string) => {
    setRows((prev) => prev.map((row, index) => (index === rowIndex ? { ...row, roofOrientation: value } : row)));
  };

  const addRow = () => {
    setRows((prev) => [...prev, { roofOrientation: '', months: Array.from({ length: 12 }, () => '') }]);
  };

  const removeRow = (rowIndex: number) => {
    setRows((prev) => prev.filter((_, index) => index !== rowIndex));
  };

  const save = async () => {
    if (!plantUuid) return;
    const payloadRows = rows
      .map((row) => ({
        roofOrientation: row.roofOrientation.trim(),
        months: row.months.map((month) => parseLocaleNumber(month)),
      }))
      .filter((row) => row.roofOrientation.length > 0);

    setSaving(true);
    try {
      const response = await savePowerPlantManualForecast(plantUuid, { year: currentYear, rows: payloadRows });
      setRows(
        response.rows.map((row) => ({
          roofOrientation: row.roofOrientation,
          months: row.months.map((item) => formatMonthValue(item)),
        })),
      );
      toast.success(payloadRows.length === 0 ? 'Tahminler silindi.' : 'Manuel tahmin kaydedildi.');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Kaydetme başarısız.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const monthlyTotals = Array.from({ length: 12 }, (_, monthIndex) => (
    rows.reduce((sum, row) => sum + (parseLocaleNumber(row.months[monthIndex] ?? '') ?? 0), 0)
  ));
  const grandTotal = monthlyTotals.reduce((sum, month) => sum + month, 0);
  const pvgisGrandTotal = pvgisMonths.reduce((sum, month) => sum + month, 0);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
      <div className="flex h-[86vh] w-[96vw] max-w-[1400px] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="text-lg font-black text-slate-900">Uretim Tahmini</h3>
            <p className="text-xs font-medium text-slate-500">Excel verisini yapıştır, yön bazlı aylık üretimi kaydet.</p>
          </div>
          <button onClick={onClose} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            Kapat
          </button>
        </div>

        <div className="border-b border-slate-100 bg-slate-50/40 px-6 py-4">
          <div className="space-y-2">
            <div className="text-[11px] font-black uppercase tracking-wider text-slate-500">Excel Yapıştırma</div>
            <div className="flex gap-2">
              <textarea
                value={pasteText}
                onChange={(event) => setPasteText(event.target.value)}
                placeholder="Excel'den satırları buraya yapıştır..."
                className="h-24 flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 outline-none focus:border-blue-400"
              />
              <button
                onClick={applyPaste}
                className="inline-flex h-10 items-center gap-1.5 self-start rounded-xl border border-blue-200 bg-blue-50 px-3 text-xs font-bold text-blue-700 hover:bg-blue-100"
              >
                <ClipboardPaste size={14} />
                Uygula
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4">
          {loading ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">Veri yükleniyor...</div>
          ) : (
            <div className="w-full overflow-x-auto rounded-2xl border border-slate-200">
              <table className="min-w-[980px] w-full border-collapse text-[10px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="sticky left-0 z-20 border-b border-slate-200 bg-slate-50 px-2 py-1.5 text-left text-[10px] font-black uppercase tracking-tight text-slate-500">Yön</th>
                  {MONTH_LABELS.map((month) => (
                    <th key={month} className="border-b border-slate-200 px-1 py-1.5 text-center text-[10px] font-black uppercase tracking-tight text-slate-500">{month}</th>
                  ))}
                  <th className="sticky right-[44px] z-20 border-b border-slate-200 bg-slate-50 px-1.5 py-1.5 text-center text-[10px] font-black uppercase tracking-tight text-slate-500">Toplam</th>
                  <th className="sticky right-0 z-20 w-11 border-b border-slate-200 bg-slate-50 px-1.5 py-1.5 text-center text-[10px] font-black uppercase tracking-tight text-slate-500">Sil</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => {
                  const total = row.months.reduce((sum, month) => sum + (parseLocaleNumber(month) ?? 0), 0);
                  return (
                    <tr key={`manual-forecast-row-${rowIndex}`} className="odd:bg-white even:bg-slate-50/40">
                      <td className="sticky left-0 z-10 border-b border-slate-100 bg-inherit px-1.5 py-1">
                        <input
                          value={row.roofOrientation}
                          onChange={(event) => updateOrientation(rowIndex, event.target.value)}
                          className="h-8 w-[112px] rounded-md border border-slate-200 bg-white px-1.5 text-[10px] font-semibold text-slate-700 outline-none focus:border-blue-400 md:w-[132px]"
                          placeholder="Örn: Güney Doğu"
                        />
                      </td>
                      {row.months.map((monthValue, monthIndex) => (
                        <td key={`${rowIndex}-${monthIndex}`} className="border-b border-slate-100 px-1 py-1">
                          <input
                            value={monthValue}
                            onChange={(event) => updateCell(rowIndex, monthIndex, event.target.value)}
                            className="h-8 w-[58px] rounded-md border border-slate-200 bg-white px-1.5 text-right text-[10px] font-semibold text-slate-700 outline-none focus:border-blue-400 md:w-[70px]"
                            placeholder="0"
                          />
                        </td>
                      ))}
                      <td className="sticky right-[44px] z-10 border-b border-slate-100 bg-inherit px-1.5 py-1 text-right text-[10px] font-black text-slate-700">
                        {numberFormatter.format(total)}
                      </td>
                      <td className="sticky right-0 z-10 w-11 border-b border-slate-100 bg-inherit px-1.5 py-1 text-center">
                        <button
                          onClick={() => removeRow(rowIndex)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100"
                          title="Satırı sil"
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-blue-50/70">
                  <td className="sticky left-0 z-10 border-t border-slate-200 bg-blue-50/70 px-2 py-1.5 text-[10px] font-black uppercase tracking-tight text-slate-700">
                    Toplam
                  </td>
                  {monthlyTotals.map((monthTotal, monthIndex) => (
                    <td key={`monthly-total-${monthIndex}`} className="border-t border-slate-200 px-1 py-1.5 text-right text-[10px] font-black text-slate-700">
                      {numberFormatter.format(monthTotal)}
                    </td>
                  ))}
                  <td className="sticky right-[44px] z-10 border-t border-slate-200 bg-blue-50/70 px-1.5 py-1.5 text-right text-[10px] font-black text-slate-900">
                    {numberFormatter.format(grandTotal)}
                  </td>
                  <td className="sticky right-0 z-10 w-11 border-t border-slate-200 bg-blue-50/70 px-1.5 py-1.5 text-center text-[10px] font-semibold text-slate-500">
                    -
                  </td>
                </tr>
                <tr className="bg-amber-50/70">
                  <td className="sticky left-0 z-10 border-t border-slate-200 bg-amber-50/70 px-2 py-1.5 text-[10px] font-black uppercase tracking-tight text-amber-800">
                    PVGIS
                  </td>
                  {pvgisMonths.map((monthTotal, monthIndex) => (
                    <td key={`pvgis-total-${monthIndex}`} className="border-t border-slate-200 px-1 py-1.5 text-right text-[10px] font-black text-amber-800">
                      {numberFormatter.format(monthTotal)}
                    </td>
                  ))}
                  <td className="sticky right-[44px] z-10 border-t border-slate-200 bg-amber-50/70 px-1.5 py-1.5 text-right text-[10px] font-black text-amber-900">
                    {numberFormatter.format(pvgisGrandTotal)}
                  </td>
                  <td className="sticky right-0 z-10 w-11 border-t border-slate-200 bg-amber-50/70 px-1.5 py-1.5 text-center text-[10px] font-semibold text-amber-500">
                    -
                  </td>
                </tr>
              </tbody>
            </table>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 px-6 py-4">
          <button
            onClick={addRow}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Plus size={14} />
            Satır Ekle
          </button>
          <button
            onClick={() => void save()}
            disabled={saving || loading}
            className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-black text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
          >
            <Save size={14} />
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PowerPlantDetailPage() {
  const { t } = useI18n();
  const { uuid_id } = useParams<{ uuid_id: string }>();
  const navigate = useNavigate();
  const headerActionsTarget = useHeaderActionsTarget();
  const [detailTab, setDetailTab] = useState('production');
  const [productionPeriod, setProductionPeriod] = useState<ProductionPeriod | null>(null);
  const [productionPending, setProductionPending] = useState(false);
  useEffect(() => { setDetailTab('production'); }, [uuid_id]);

  const [cards, setCards]     = useState<DetailCards | null>(null);
  const [charts, setCharts]   = useState<DetailCharts | null>(null);
  const [expectedHourly, setExpectedHourly] = useState<Array<{ date_name: string; power_total: number }> | null>(null);
  const [openMeteoExpectedHourly, setOpenMeteoExpectedHourly] = useState<Array<{ date_name: string; power_total: number }> | null>(null);
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [solarPanelSummary, setSolarPanelSummary] = useState<SolarPanelSummary[] | null>(null);
  const [cardsLoading, setCardsLoading]   = useState(true);
  usePageTitle(cardsLoading ? null : cards?.plantName);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [solarPanelLoading, setSolarPanelLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [modbusLiveOpen, setModbusLiveOpen] = useState(false);
  const [contactCardsOpen, setContactCardsOpen] = useState(false);
  const [tagsModalOpen, setTagsModalOpen] = useState(false);
  const [manualForecastOpen, setManualForecastOpen] = useState(false);
  const [tagRefreshKey, setTagRefreshKey] = useState(0);

  useEffect(() => {
    if (!uuid_id) return;
    const controller = new AbortController();
    const { signal } = controller;
    setCards(null); setCharts(null); setWeather(null); setSolarPanelSummary(null);
    setExpectedHourly(null); setOpenMeteoExpectedHourly(null); setError('');
    setCardsLoading(true); setChartsLoading(true); setSolarPanelLoading(true);
    setProductionPeriod(null); setProductionPending(false);
    const hourFormatter = new Intl.DateTimeFormat('tr-TR', { hour: '2-digit', minute: '2-digit' });

    void fetchDetailCards(uuid_id, signal)
      .then(data => {
        if (signal.aborted) return;
        setCards(data);
        if (data.plantLatitude && data.plantLongitude) {
          void fetchWeather(`${Number(data.plantLatitude)},${Number(data.plantLongitude)}`, signal)
            .then(weather => { if (!signal.aborted) setWeather(weather); })
            .catch(() => { /* Weather is optional. */ });
        }
      })
      .catch(() => { if (!signal.aborted) setError(t('powerPlants.detailLoadError')); })
      .finally(() => { if (!signal.aborted) setCardsLoading(false); });

    void fetchDetailCharts(uuid_id, signal)
      .then(data => { if (!signal.aborted) setCharts(data); })
      .catch(() => { /* Charts are optional. */ })
      .finally(() => { if (!signal.aborted) setChartsLoading(false); });

    void fetchPvlibExpectedHourly(uuid_id, signal)
      .then(series => { if (!signal.aborted) setExpectedHourly(series.map(item => ({
        date_name: hourFormatter.format(new Date(item.timestamp)), power_total: item.expectedKwh,
      }))); })
      .catch(() => { if (!signal.aborted) setExpectedHourly(null); });

    void fetchOpenMeteoExpectedHourly(uuid_id, signal)
      .then(series => { if (!signal.aborted) setOpenMeteoExpectedHourly(series.map(item => ({
        date_name: hourFormatter.format(new Date(item.timestamp)), power_total: item.expectedKwh,
      }))); })
      .catch(() => { if (!signal.aborted) setOpenMeteoExpectedHourly(null); });

    void fetchPowerPlantDetailSolarPanelSummary(uuid_id, signal)
      .then(data => { if (!signal.aborted) setSolarPanelSummary(data); })
      .catch(() => { if (!signal.aborted) setSolarPanelSummary([]); })
      .finally(() => { if (!signal.aborted) setSolarPanelLoading(false); });
    return () => controller.abort();
  }, [uuid_id, t]);

  const productionChartData = useMemo(() => charts ? {
    hourly: charts.hourly, daily: charts.daily, monthly: charts.monthly, yearly: charts.yearly,
  } : null, [charts]);
  const fetchProductionDrill = useCallback((year: number, month: number | null, day: number | null) =>
    fetchPlantPowerDrillChart(uuid_id ?? '', year, month, day), [uuid_id]);

  if (error) return <LoadRetry />;

  const menuItems = [
    {
      label: t('common.edit'),
      icon: <Settings size={14} />,
      variant: 'info' as const,
      onClick: () => setSettingsOpen(true)
    },
    {
      label: 'İletişim Kartı',
      icon: <PhoneCall size={14} />,
      variant: 'warning' as const,
      onClick: () => setContactCardsOpen(true),
    },
    {
      label: 'Santral Sağlığı ve Raporu',
      icon: <Activity size={14} />,
      variant: 'success' as const,
      onClick: () => navigate(`/dashboard/powerplants/${uuid_id}/health-analysis`),
    },
    {
      label: 'Canlı Modbus Verileri',
      icon: <Activity size={14} />,
      variant: 'info' as const,
      onClick: () => setModbusLiveOpen(true),
    },
    {
      label: 'PVGIS Analizi',
      icon: <BarChart3 size={14} />,
      variant: 'default' as const,
      onClick: () => navigate(`/dashboard/powerplants/${uuid_id}/pvgis-analysis`)
    },
    {
      label: 'Forecast.Solar Tahmini',
      icon: <Sun size={14} />,
      variant: 'warning' as const,
      onClick: () => navigate(`/dashboard/powerplants/${uuid_id}/forecast-solar-analysis`)
    },
    {
      label: 'Üretim Tahmini',
      icon: <ClipboardPaste size={14} />,
      variant: 'info' as const,
      onClick: () => setManualForecastOpen(true),
    },
    {
      label: 'Raporlar',
      icon: <BarChart3 size={14} />,
      variant: 'default' as const,
      onClick: () => {},
      children: [
        {
          label: 'Günlük Santral Raporu',
          icon: <BarChart3 size={14} />,
          variant: 'default' as const,
          onClick: () => navigate('/dashboard/reports', {
            state: {
              prefillReportId: 'plant-daily',
              prefillPowerPlantUuids: uuid_id ? [uuid_id] : [],
            },
          }),
        },
        {
          label: 'Aylık Santral Raporu',
          icon: <BarChart3 size={14} />,
          variant: 'default' as const,
          onClick: () => navigate('/dashboard/reports', {
            state: {
              prefillReportId: 'plant-monthly',
              prefillPowerPlantUuids: uuid_id ? [uuid_id] : [],
            },
          }),
        },
        {
          label: 'Özel Aylık Santral Raporu',
          icon: <BarChart3 size={14} />,
          variant: 'default' as const,
          onClick: () => navigate('/dashboard/reports', {
            state: {
              prefillReportId: 'plant-monthly-custom',
              prefillPowerPlantUuids: uuid_id ? [uuid_id] : [],
            },
          }),
        },
        {
          label: 'Bütçe vs. Gerçekleşen Üretim',
          icon: <BarChart3 size={14} />,
          variant: 'default' as const,
          onClick: () => navigate('/dashboard/reports', {
            state: {
              prefillReportId: 'plant-budget-vs-actual',
              prefillPowerPlantUuids: uuid_id ? [uuid_id] : [],
            },
          }),
        },
      ],
    },
    {
      label: `${t('management.tabs.tags')} Düzenle`,
      icon: <Tag size={14} />,
      variant: 'success' as const,
      onClick: () => setTagsModalOpen(true)
    }
  ];

  return (
    <div className="flex flex-col gap-2 overflow-y-auto flex-1 min-h-0 pb-6 text-slate-800">
      {headerActionsTarget && createPortal(<>
        {cards && (
          <div className="max-w-64 min-w-0 overflow-x-auto">
            <ObjectTagsPanel
              key={tagRefreshKey}
              categoryCode="power_plant"
              objectId={cards.plantId}
              emptyText={t('management.tags.noAssignedTags')}
              hideEdit={true}
              showTitle={false}
              className="w-fit border-slate-200 bg-slate-50/60 px-3 py-1.5 shadow-none"
            />
          </div>
        )}
        <div className="flex items-center justify-end gap-3 flex-shrink-0">
          <DropdownMenu
            title={t('management.users.actions')}
            items={menuItems}
            align="right"
          />
        </div>
      </>, headerActionsTarget)}

      {cardsLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 shadow-sm ring-1 ring-black/5 rounded-2xl overflow-hidden bg-white/50 backdrop-blur-sm p-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-40 animate-pulse rounded-2xl bg-slate-100" />
          ))}
        </div>
      ) : cards ? (
        <DetailStatCards cards={cards} weather={weather} plantUuid={uuid_id} />
      ) : null}

      <div className="min-w-0 rounded-xl border-2 border-slate-300 bg-white shadow-sm">
        <div role="tablist" aria-label="Santral detay bölümleri" className="flex gap-1 overflow-x-auto rounded-t-[10px] border-b border-slate-300 bg-slate-50 p-1">
          {[
            { id: 'production', label: 'Üretim', Icon: BarChart3, disabled: false },
            { id: 'flow', label: 'Akış', Icon: Activity, disabled: false },
            { id: 'panels', label: 'Panel Bilgisi', Icon: PanelsTopLeft, disabled: solarPanelLoading || !solarPanelSummary?.length },
            { id: 'smartlogger', label: 'SmartLogger', Icon: Server, disabled: !cards?.smartloggerDeviceCount },
            { id: 'meter', label: 'Power Meter', Icon: Gauge, disabled: !cards?.meterCount },
            { id: 'sensors', label: 'Sensör', Icon: ThermometerSun, disabled: !cards?.sensorsCount },
          ].map(({ id, label, Icon, disabled }) => <button
            key={id} id={`plant-tab-${id}`} type="button" role="tab"
            aria-selected={detailTab === id} aria-controls={`plant-panel-${id}`}
            tabIndex={detailTab === id ? 0 : -1}
            disabled={disabled}
            title={disabled ? `${label} tanımı bulunmuyor` : label}
            onClick={() => setDetailTab(id)}
            onKeyDown={event => {
              if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              const tabs = Array.from(event.currentTarget.parentElement!.querySelectorAll<HTMLButtonElement>('[role="tab"]:not(:disabled)'));
              const index = tabs.indexOf(event.currentTarget);
              const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
              tabs[next].focus(); tabs[next].click();
            }}
            className={`inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-500 ${detailTab === id ? 'border-blue-600 bg-blue-600 text-white shadow-sm' : 'border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-800'}`}
          ><Icon size={14} />{label}</button>)}
        </div>
        <div key={`${uuid_id}:${detailTab}`} role="tabpanel" id={`plant-panel-${detailTab}`} aria-labelledby={`plant-tab-${detailTab}`} tabIndex={0} className="min-w-0 space-y-3 rounded-b-[10px]">
      {detailTab === 'flow' && <EnergyFlowPage embedded />}
      {detailTab === 'sensors' && uuid_id && <PlantSensorsPanel plantUuid={uuid_id} />}
      {detailTab === 'panels' && <SolarPanelInfoCard
        title="Panel Bilgisi"
        data={solarPanelSummary}
        loading={solarPanelLoading}
        collapsible={false}
      />}

      {detailTab === 'smartlogger' && cards && cards.smartloggerDeviceCount > 0 && uuid_id && <SmartloggerPanel plantUuid={uuid_id} collapsible={false} />}
      {detailTab === 'meter' && cards && cards.meterCount > 0 && uuid_id && <PowerMeterPanel key={uuid_id} ownerUuid={uuid_id} scope="plant" collapsible={false} />}

      {detailTab === 'production' && (chartsLoading ? (
        <div className="h-64 animate-pulse rounded-2xl bg-gray-100" />
      ) : charts ? (
        <div className="flex flex-col gap-6">
          <PowerBarChart
            key={uuid_id}
            onPeriodChange={setProductionPeriod}
            onDrillLoadingChange={setProductionPending}
            data={productionChartData!}
            expectedHourly={expectedHourly ?? undefined}
            openMeteoExpectedHourly={openMeteoExpectedHourly ?? undefined}
            initialTab="Hourly"
            drillFetcher={fetchProductionDrill}
          />

          {charts.performance.length > 0 && cards && cards.sensorsCount > 0 && (
            <PerformanceChart data={charts.performance} />
          )}

          <InverterLineChart
            plantUuid={uuid_id ?? ''}
            hourly={charts.inverterHourly}
            daily={charts.inverterDaily}
            monthly={charts.inverterMonthly}
            yearly={charts.inverterYearly}
          />
          {uuid_id && <InverterProductionCards key={uuid_id} plantUuid={uuid_id} period={productionPeriod} pending={productionPending} />}
        </div>
      ) : null)}
        </div>
      </div>

      {settingsOpen && uuid_id && (
        <PlantSettingsModal uuid_id={uuid_id} onClose={() => setSettingsOpen(false)} />
      )}
      {modbusLiveOpen && uuid_id && <PlantModbusLiveModal uuid={uuid_id} onClose={() => setModbusLiveOpen(false)} />}

      {contactCardsOpen && uuid_id && (
        <PlantContactCardsModal uuid_id={uuid_id} onClose={() => setContactCardsOpen(false)} />
      )}

      {tagsModalOpen && cards && (
        <ObjectTagsPanel 
          categoryCode="power_plant" 
          objectId={cards.plantId} 
          onlyModal={true}
          onClose={() => setTagsModalOpen(false)}
          onSaveSuccess={() => setTagRefreshKey(prev => prev + 1)}
        />
      )}

      <ManualForecastModal
        open={manualForecastOpen}
        onClose={() => setManualForecastOpen(false)}
        plantUuid={uuid_id}
      />
    </div>
  );
}
